import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import {
  FileText,
  AlertTriangle,
  Clock,
  RefreshCw,
  Mail,
  Phone,
  Calendar,
  CheckCircle,
  XCircle,
  Loader2,
  Send,
  Building
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const LeaseStatusDashboard = () => {
  const { token, isManager } = useAuth();
  const [loading, setLoading] = useState(true);
  const [statusSummary, setStatusSummary] = useState(null);
  const [renewalReminders, setRenewalReminders] = useState([]);
  const [sendingReminder, setSendingReminder] = useState(null);
  const [confirmSend, setConfirmSend] = useState(null);
  const [updating, setUpdating] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statusRes, remindersRes] = await Promise.all([
        axios.get(`${API}/leases/status-summary`, { headers }),
        axios.get(`${API}/leases/renewal-reminders`, { headers, params: { days_ahead: 60 } })
      ]);
      setStatusSummary(statusRes.data);
      setRenewalReminders(remindersRes.data);
    } catch (error) {
      toast.error("Failed to load lease data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const handleAutoUpdateStatuses = async () => {
    setUpdating(true);
    try {
      const res = await axios.post(`${API}/leases/auto-update-statuses`, {}, { headers });
      toast.success(`Updated ${res.data.updated} leases`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update statuses");
    } finally {
      setUpdating(false);
    }
  };

  const handleSendReminder = async (leaseId) => {
    setSendingReminder(leaseId);
    try {
      await axios.post(`${API}/leases/${leaseId}/send-renewal-reminder`, {}, { headers });
      toast.success("Renewal reminder sent!");
      setConfirmSend(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reminder");
    } finally {
      setSendingReminder(null);
    }
  };

  const getUrgencyBadge = (urgency) => {
    switch (urgency) {
      case "high":
        return <Badge className="bg-red-100 text-red-700">Urgent</Badge>;
      case "medium":
        return <Badge className="bg-amber-100 text-amber-700">Soon</Badge>;
      default:
        return <Badge className="bg-blue-100 text-blue-700">Upcoming</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-green-700" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="lease-status-dashboard">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{statusSummary?.summary.active || 0}</p>
                <p className="text-sm text-slate-500">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{statusSummary?.summary.expiring_soon || 0}</p>
                <p className="text-sm text-slate-500">Expiring Soon</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <RefreshCw className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{statusSummary?.summary.month_to_month || 0}</p>
                <p className="text-sm text-slate-500">Month-to-Month</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{statusSummary?.summary.expired || 0}</p>
                <p className="text-sm text-slate-500">Expired</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      {isManager && (
        <div className="flex gap-2">
          <Button
            onClick={handleAutoUpdateStatuses}
            disabled={updating}
            variant="outline"
            data-testid="auto-update-statuses"
          >
            {updating ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Auto-Update Statuses
          </Button>
        </div>
      )}

      {/* Renewal Reminders */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Lease Renewal Reminders
              </CardTitle>
              <CardDescription>
                Leases expiring in the next 60 days
              </CardDescription>
            </div>
            <Badge variant="outline">{renewalReminders.length} leases</Badge>
          </div>
        </CardHeader>
        <CardContent>
          {renewalReminders.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-400" />
              <p>No leases expiring in the next 60 days</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tenant</TableHead>
                    <TableHead>Property</TableHead>
                    <TableHead>End Date</TableHead>
                    <TableHead>Days Left</TableHead>
                    <TableHead>Rent</TableHead>
                    <TableHead>Urgency</TableHead>
                    {isManager && <TableHead className="text-right">Action</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {renewalReminders.map((reminder) => (
                    <TableRow key={reminder.lease_id} data-testid={`reminder-${reminder.lease_id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{reminder.tenant_name}</p>
                          <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
                            {reminder.tenant_email && (
                              <span className="flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {reminder.tenant_email}
                              </span>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building className="w-4 h-4 text-slate-400" />
                          <span className="text-sm">{reminder.property_address}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          {reminder.end_date}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={`font-bold ${
                          reminder.days_remaining <= 30 ? "text-red-600" :
                          reminder.days_remaining <= 45 ? "text-amber-600" : "text-blue-600"
                        }`}>
                          {reminder.days_remaining} days
                        </span>
                      </TableCell>
                      <TableCell className="font-medium">
                        ${reminder.monthly_rent?.toLocaleString()}/mo
                      </TableCell>
                      <TableCell>
                        {getUrgencyBadge(reminder.urgency)}
                      </TableCell>
                      {isManager && (
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setConfirmSend(reminder)}
                            disabled={!reminder.tenant_email || sendingReminder === reminder.lease_id}
                            data-testid={`send-reminder-${reminder.lease_id}`}
                          >
                            {sendingReminder === reminder.lease_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Send className="w-4 h-4 mr-1" />
                                Send
                              </>
                            )}
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Expiring Soon Details */}
      {statusSummary?.expiring_leases?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-amber-700">Expiring Leases Detail</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {statusSummary.expiring_leases.map((lease) => (
                <div 
                  key={lease.lease_id}
                  className="p-4 border rounded-lg flex items-center justify-between hover:bg-slate-50"
                >
                  <div>
                    <p className="font-medium">{lease.tenant_name}</p>
                    <p className="text-sm text-slate-500">{lease.property_address}</p>
                  </div>
                  <div className="text-right">
                    <p className={`font-bold ${
                      lease.days_remaining <= 30 ? "text-red-600" : "text-amber-600"
                    }`}>
                      {lease.days_remaining} days
                    </p>
                    <p className="text-sm text-slate-500">Expires: {lease.end_date}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Confirmation Dialog */}
      <AlertDialog open={!!confirmSend} onOpenChange={() => setConfirmSend(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send Renewal Reminder?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmSend && (
                <>
                  Send a renewal reminder email to <strong>{confirmSend.tenant_name}</strong> at{" "}
                  <strong>{confirmSend.tenant_email}</strong>?
                  <br /><br />
                  Their lease at <strong>{confirmSend.property_address}</strong> expires on{" "}
                  <strong>{confirmSend.end_date}</strong> ({confirmSend.days_remaining} days).
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmSend && handleSendReminder(confirmSend.lease_id)}
              className="bg-green-700 hover:bg-green-800"
            >
              <Send className="w-4 h-4 mr-2" />
              Send Reminder
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LeaseStatusDashboard;
